/**
 * Flatten LaTeX the model still emits into readable plain markdown.
 *
 * The system prompt forbids LaTeX, but models leak it anyway (\frac, $…$,
 * \sigma), and the chat has no math renderer — raw \frac{a}{b} in a sidebar is
 * unreadable. Rather than pull in KaTeX for the occasional stray formula, this
 * rewrites the handful of constructs that actually show up into inline text:
 * fractions become `a / b`, greek/operators become their unicode character,
 * and the `$` delimiters go away.
 */

const GREEK: Record<string, string> = {
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  Delta: 'Δ',
  epsilon: 'ε',
  eta: 'η',
  theta: 'θ',
  lambda: 'λ',
  mu: 'μ',
  pi: 'π',
  rho: 'ρ',
  sigma: 'σ',
  Sigma: 'Σ',
  tau: 'τ',
  phi: 'φ',
  omega: 'ω',
  approx: '≈',
  times: '×',
  cdot: '·',
  pm: '±',
  leq: '≤',
  geq: '≥',
  neq: '≠',
  rightarrow: '→',
  to: '→',
  ll: '≪',
  gg: '≫',
}

/** `\frac{a}{b}` → `(a) / (b)`, innermost first so nesting collapses too. */
const flattenFractions = (input: string): string => {
  const pattern = /\\(?:d)?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/
  let out = input
  for (let guard = 0; guard < 12 && pattern.test(out); guard++) {
    out = out.replace(
      pattern,
      (_m, num: string, den: string) => `(${num.trim()}) / (${den.trim()})`
    )
  }
  return out
}

const stripWrappers = (input: string): string =>
  input
    .replace(/\^\s*\{?\s*\\circ\s*\}?|\\(?:circ|degree)(?![A-Za-z])/g, '°')
    // \text{…}, \mathrm{…}, \operatorname{…} keep only their content
    .replace(
      /\\(?:text|mathrm|mathit|mathbf|operatorname|mbox)\s*\{([^{}]*)\}/g,
      '$1'
    )
    // \left( \right] etc. — the delimiter itself is enough
    .replace(/\\(?:left|right|big|Big|bigg|Bigg)\s*/g, '')
    // \sqrt{x} → sqrt(x)
    .replace(/\\sqrt\s*\{([^{}]*)\}/g, 'sqrt($1)')
    .replace(/\\ddot(?![A-Za-z])\s*(?:\{([^{}]*)\}|([A-Za-z]))/g, '$1$2\u0308')
    .replace(/\\dot(?![A-Za-z])\s*(?:\{([^{}]*)\}|([A-Za-z]))/g, '$1$2\u0307')
    // subscripts/superscripts: keep the token, drop the braces
    .replace(/([_^])\s*\{([^{}]*)\}/g, '$1$2')

const replaceSymbols = (input: string): string =>
  input.replace(
    /\\([A-Za-z]+)/g,
    (match, name: string) => GREEK[name] ?? match.slice(1)
  )

/** Rewrite one math body (already stripped of its `$` delimiters). */
const flattenMath = (body: string): string =>
  replaceSymbols(stripWrappers(flattenFractions(body)))
    .replace(/\s*&\s*/g, ' ')
    .replace(/\\\\/g, ' ')
    .replace(/\{|\}/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()

/** A `$…$` body counts as math when it uses LaTeX syntax, is a single
 *  identifier (`$L$`, `$G$`), or is a bare number (`$0.4535924$`). "between
 *  $5k and $12k" is money: that span has neither, so the dollars stay. */
const LATEX_SYNTAX = /[\\^_{}]/
const MATH_IDENTIFIER = /^[A-Za-z][A-Za-z0-9]*$/
const MATH_NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)$/

const isMathSpan = (body: string): boolean => {
  const trimmed = body.trim()
  return (
    LATEX_SYNTAX.test(trimmed) ||
    MATH_IDENTIFIER.test(trimmed) ||
    MATH_NUMBER.test(trimmed)
  )
}

/**
 * Replace `$$…$$` and `$…$` spans with flattened text. Code spans/fences are
 * left alone — a `$` inside a shell snippet is not math.
 */
const flattenLatexSpans = (
  text: string,
  displayMath: (body: string) => string
): string => {
  const segments = text.split(/(```[\s\S]*?```|`[^`]*`)/g)
  return segments
    .map((segment, i) => {
      if (i % 2 === 1) return segment // the captured code span
      return segment
        .replace(/\$\$([\s\S]+?)\$\$/g, (_m, body: string) => displayMath(body))
        .replace(/\$([^$\n]+?)\$/g, (match, body: string) =>
          isMathSpan(body) ? flattenMath(body) : match
        )
        .replace(/\\\[([\s\S]+?)\\\]/g, (_m, body: string) => displayMath(body))
        .replace(/\\\(([\s\S]+?)\\\)/g, (_m, body: string) => flattenMath(body))
    })
    .join('')
}

export const flattenLatex = (text: string): string =>
  flattenLatexSpans(text, (body) => `\`${flattenMath(body)}\``)

/** Same rewrite as flattenLatex, but display math stays plain text — for
 *  streaming glyph maps that are not markdown. */
export const flattenLatexPlain = (text: string): string =>
  flattenLatexSpans(text, flattenMath)

/**
 * Put back the spaces the model omits around its own emphasis markers.
 *
 * Models write `**Performance Gap:**+51.44 **GPM**(operating at**+45.18` — no
 * space either side of the `**`. Markdown is then parsed correctly and the
 * emphasis is stripped correctly, so the rendered line reads
 * "Performance Gap:+51.44 GPM(operating at+45.18", with words fused. It looks
 * like a renderer bug and is not one: the space was never in the source.
 *
 * Only boundaries where a marker touches an alphanumeric or an opening bracket
 * are padded, so deliberate intra-word emphasis (`re**tro**fit`) is left alone.
 */
export const spaceEmphasis = (text: string): string => {
  // The whole emphasis span, so an opening marker is never mistaken for a
  // closing one — the mistake that pads the inside instead of the outside.
  const span = /(\*\*|__)(?=\S)([\s\S]*?\S)\1/g
  // Characters that legitimately abut a marker: whitespace, and the punctuation
  // that naturally follows or precedes a phrase.
  const closesFine = /[\s.,;:!?)\]}'"]/
  const opensFine = /[\s([{'"]/
  const word = /[A-Za-z0-9]/

  const segments = text.split(/(```[\s\S]*?```|`[^`]*`)/g)
  return segments
    .map((segment, i) => {
      if (i % 2 === 1) return segment
      return segment.replace(
        span,
        (match, _marker, body: string, offset: number) => {
          const before = segment[offset - 1]
          const after = segment[offset + match.length]
          // Intra-word emphasis is a single word wrapped mid-word (`re**tro**fit`),
          // which CommonMark does not emphasise at all — padding it would change
          // the text. A body containing spaces is a phrase the model simply failed
          // to space, even when letters happen to sit either side of it.
          const intraWord =
            !/\s/.test(body) &&
            before !== undefined &&
            after !== undefined &&
            word.test(before) &&
            word.test(after)
          if (intraWord) return match
          const padBefore = before !== undefined && !opensFine.test(before)
          const padAfter = after !== undefined && !closesFine.test(after)
          return `${padBefore ? ' ' : ''}${match}${padAfter ? ' ' : ''}`
        }
      )
    })
    .join('')
}

/**
 * Everything the chat needs doing to a model's text before it is rendered as
 * markdown: flatten the LaTeX it was told not to emit, then repair the spacing
 * around its emphasis. One entry point so a caller cannot apply half of it.
 */
export const readableMarkdown = (text: string): string =>
  spaceEmphasis(flattenLatex(text))
